import { useEffect, useMemo } from "react";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { SortByOptions } from "../assistantOverview/ClassActivityFilterBar";
import { ActivityListWithPopups } from "./ActivityListWithPopups";
import type { ClassActivity } from "./ClassActivityCardsOverview.types";
import { getTestLanguageName, getVocabTrainingLanguageName } from "../../helpers/activityLanguageHelpers";
import { useClassActivityData } from "../../hooks/useClassActivityData";

interface ClassActivityCardsOverviewProps {
    classFilter: string;
    languageFilter: string;
    sortBy: SortByOptions;
    onAvailableLanguagesChange: (languages: string[]) => void;
}

export const ClassActivityCardsOverview = (props: ClassActivityCardsOverviewProps) => {
    const {
        activities: allActivities,
        testOps,
        vocabTrainingOps,
        schoolClasses,
        studentTestContentWrapper,
        userTests,
        userTestsLoadError,
        userUsedBooksInfo,
        userUsedOwnSubjectsInfo,
    } = useClassActivityData();

    // Kept as useMemo because it feeds the useEffect below — without memo, the effect
    // fires every render since the array reference would change.
    const availableLanguages = useMemo((): string[] => {
        const languages = new Set<string>();

        userTests.forEach((test) => {
            const lang = getTestLanguageName(test);
            if (lang) languages.add(lang);
        });

        vocabTrainingOps.vocabTrainings.forEach((training) => {
            const lang = getVocabTrainingLanguageName(training, { userUsedBooksInfo, userUsedOwnSubjectsInfo });
            if (lang) languages.add(lang);
        });

        return Array.from(languages).sort();
    }, [userTests, vocabTrainingOps.vocabTrainings, userUsedBooksInfo, userUsedOwnSubjectsInfo]);

    const onAvailableLanguagesChange = props.onAvailableLanguagesChange;
    useEffect(() => {
        onAvailableLanguagesChange(availableLanguages);
    }, [availableLanguages, onAvailableLanguagesChange]);

    const activities = useMemo((): ClassActivity[] => {
        const matchesClassFilter = (activity: ClassActivity): boolean => {
            if (!props.classFilter) return true;
            if (activity.category === ClassActivityCategory.TEST) {
                return activity.testData.groupId === props.classFilter;
            }
            if (activity.category === ClassActivityCategory.VOCAB_TRAINING) {
                return activity.vocabTrainingData.groupId === props.classFilter;
            }
            return true;
        };

        const matchesLanguageFilter = (activity: ClassActivity): boolean => {
            if (!props.languageFilter) return true;
            if (activity.category === ClassActivityCategory.TEST) {
                return getTestLanguageName(activity.testData) === props.languageFilter;
            }
            if (activity.category === ClassActivityCategory.VOCAB_TRAINING) {
                return (
                    getVocabTrainingLanguageName(activity.vocabTrainingData, {
                        userUsedBooksInfo,
                        userUsedOwnSubjectsInfo,
                    }) === props.languageFilter
                );
            }
            return true;
        };

        return allActivities
            .filter((activity) => matchesClassFilter(activity) && matchesLanguageFilter(activity))
            .sort((a, b) =>
                props.sortBy === SortByOptions.CREATED_ASC ? a.sortDate - b.sortDate : b.sortDate - a.sortDate
            );
    }, [
        allActivities,
        userUsedBooksInfo,
        userUsedOwnSubjectsInfo,
        props.classFilter,
        props.languageFilter,
        props.sortBy,
    ]);

    return (
        <ActivityListWithPopups
            activities={activities}
            schoolClasses={schoolClasses}
            studentTestContentWrapper={studentTestContentWrapper}
            classFilter={props.classFilter}
            languageFilter={props.languageFilter}
            testOps={testOps}
            vocabTrainingOps={vocabTrainingOps}
            userTestsLoadError={userTestsLoadError}
        />
    );
};
